/**
 * Customer auth: OTP send, OTP verify, silent refresh, logout, and "who am I".
 *
 * The token discipline is set by client.ts and this module must not weaken it:
 * the access token goes into the module-scope variable via `setAccessToken` and
 * nowhere else, and the refresh token is never seen by JavaScript at all — it
 * arrives and leaves as an httpOnly cookie. There is no storage call in this
 * file, and an eslint rule makes sure there never is.
 */
import { api, clearAccessToken, setAccessToken } from './client';
import type {
  AccessTokenResponse,
  CustomerAuthResponse,
  CustomerProfile,
  OtpSendResponse,
} from './types';

export const AUTH_OTP_PATH = '/api/v1/auth/otp';
export const AUTH_OTP_VERIFY_PATH = '/api/v1/auth/otp/verify';
export const AUTH_REFRESH_PATH = '/api/v1/auth/refresh';
export const AUTH_LOGOUT_PATH = '/api/v1/auth/logout';
export const AUTH_ME_PATH = '/api/v1/auth/me';

/**
 * Requests a code. The answer is identical for known and unknown numbers — the
 * backend does that on purpose so this endpoint is not a customer-list oracle,
 * and the UI must not undo it by rendering "welcome back".
 */
export function sendOtp(phone: string): Promise<OtpSendResponse> {
  return api.post<OtpSendResponse>(AUTH_OTP_PATH, { phone });
}

/**
 * Verifies the code and opens a session.
 *
 * Also the moment anonymous work is claimed: the upload made before signing in
 * belongs to the `ps_session` cookie, and the server reassigns it to the new
 * account here. Nothing needs to be passed for that to happen — but the caller
 * must not navigate away from the `?u=` URL across this round trip, or the id
 * is lost from the client's side even though the server kept the file.
 */
export async function verifyOtp(args: {
  phone: string;
  code: string;
  name?: string | undefined;
}): Promise<CustomerAuthResponse> {
  const response = await api.post<CustomerAuthResponse>(AUTH_OTP_VERIFY_PATH, {
    phone: args.phone,
    code: args.code,
    ...(args.name ? { name: args.name } : {}),
  });
  setAccessToken(response.access_token);
  return response;
}

/**
 * Trades the refresh cookie for a new access token.
 *
 * Throws a 401 ApiError when there is no cookie, which is the normal state for
 * a first-time visitor — callers treat that as "anonymous", not as a failure.
 *
 * CONCURRENT CALLS SHARE ONE REQUEST, and that is not an optimisation. The
 * backend rotates the refresh token on every use and treats a second
 * presentation of an already-rotated token as a leak — it revokes the whole
 * family. Two overlapping refreshes (React StrictMode's double-invoked effect
 * is the obvious one, a 401 retry racing the scheduled refresh is the subtle
 * one) would therefore log the customer out. The in-flight promise makes that
 * impossible.
 */
let inflightRefresh: Promise<AccessTokenResponse> | null = null;

export function refreshSession(): Promise<AccessTokenResponse> {
  if (inflightRefresh) return inflightRefresh;
  inflightRefresh = api
    .post<AccessTokenResponse>(AUTH_REFRESH_PATH)
    .then((response) => {
      setAccessToken(response.access_token);
      return response;
    })
    .finally(() => {
      inflightRefresh = null;
    });
  return inflightRefresh;
}

export function fetchMe(signal?: AbortSignal): Promise<CustomerProfile> {
  return api.get<CustomerProfile>(AUTH_ME_PATH, signal ? { signal } : undefined);
}

/**
 * Sets the signed-in customer's own name.
 *
 * Separate from verify because the sign-in form cannot know whether it needs a
 * name until the code has been checked: the OTP endpoints answer identically
 * for a known and an unknown number so they are not a customer-list oracle. So
 * the profile that comes back from verify is the first honest chance to ask.
 */
export function updateMyName(name: string): Promise<CustomerProfile> {
  return api.patch<CustomerProfile>(AUTH_ME_PATH, { name });
}

export async function logout(): Promise<void> {
  try {
    await api.post<unknown>(AUTH_LOGOUT_PATH);
  } finally {
    // Drop the in-memory token even if the revoke call failed: leaving a live
    // token in a tab the user believes is signed out is the worse outcome.
    clearAccessToken();
  }
}
